/**
 * Pure module-mapping cores for the sidebar (`sidebarModuleItems` /
 * `allModulesSorted`). The `useMemo` wrappers stay in layout/Sidebar.tsx so
 * memo identity is unchanged.
 */

import type { ModuleSidebarPreference, WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import { isSummaryModuleType, getModuleMeta, normalizeStaleModuleLabel } from '@/types/modules';
import type { SidebarModuleItem } from './types';

/**
 * Map raw workspace modules into deduped sidebar items.
 *
 * Deduplicate by type+label — keep the most recently updated module for each.
 * Page modules (report, html_viewer) are exempt: each page is a unique document.
 */
export function buildSidebarModuleItems(
  workspaceModules: WorkspaceModule[],
  modulePreferenceById: Map<string, ModuleSidebarPreference>,
): SidebarModuleItem[] {
  const deduped = new Map<string, WorkspaceModule>();
  const pages: WorkspaceModule[] = [];
  for (const module of workspaceModules) {
    // Skip summary/utility modules — they're internal, not user-visible in sidebar
    if (isSummaryModuleType(module.type)) continue;
    // Legacy Drive sub-types no longer exist — skip if any somehow remain
    if (module.type === 'photos' || module.type === 'gallery' || module.type === 'docs' || module.type === 'note_list') continue;
    // place_list modules are accessed through the Places container, not shown separately
    if (module.type === 'place_list' && workspaceModules.some(m => m.type === 'places' && !m.archivedAt)) continue;
    if (module.type === 'report' || module.type === 'html_viewer') {
      pages.push(module);
      continue;
    }
    const key = `${module.type}::${(module.label || '').trim().toLowerCase()}`;
    const existing = deduped.get(key);
    if (!existing || module.updatedAt > existing.updatedAt) {
      deduped.set(key, module);
    }
  }
  // Pages order is now handled by the API (newest-first)
  const allModules = [...Array.from(deduped.values()), ...pages];
  return allModules.map((module) => {
    const preference = modulePreferenceById.get(module.id);
    const meta = getModuleMeta(module.type as Parameters<typeof getModuleMeta>[0]);
    // User-set label always wins; fall back to the catalog default, then 'Untitled'.
    // (#3598) Standard modules now honor a custom label so renames show in the sidebar.
    // A stale pre-rename default (e.g. task_list's old "Tasks") is dropped so it
    // falls through to the catalog default, matching the pane/header surfaces.
    const displayName =
      normalizeStaleModuleLabel(module.type as Parameters<typeof getModuleMeta>[0], module.label) ||
      meta?.defaultLabel ||
      'Untitled';

    return {
      module,
      preference,
      displayName,
    };
  });
}

/**
 * Sort sidebar module items: pinned modules first, then unpinned modules.
 * Within pinned modules, respect explicit pinOrder if present. Within each
 * bucket, keep module position ordering (then alphabetical fallback). Pages
 * keep their pre-sorted newest-first order.
 */
export function sortSidebarModules(sidebarModuleItems: SidebarModuleItem[]): SidebarModuleItem[] {
  // Separate pages from non-pages — pages keep their pre-sorted newest-first order
  const pageTypes = new Set(['report', 'html_viewer']);
  const pages = sidebarModuleItems.filter(item => pageTypes.has(item.module.type));
  const nonPages = sidebarModuleItems.filter(item => !pageTypes.has(item.module.type));

  // Sort non-pages by pin status, then position, then name
  nonPages.sort((left, right) => {
    const leftPinned = Boolean(left.preference?.pinned);
    const rightPinned = Boolean(right.preference?.pinned);
    if (leftPinned !== rightPinned) return leftPinned ? -1 : 1;

    if (leftPinned && rightPinned) {
      const leftPinOrder = typeof left.preference?.pinOrder === 'number'
        ? left.preference.pinOrder
        : Number.MAX_SAFE_INTEGER;
      const rightPinOrder = typeof right.preference?.pinOrder === 'number'
        ? right.preference.pinOrder
        : Number.MAX_SAFE_INTEGER;
      if (leftPinOrder !== rightPinOrder) return leftPinOrder - rightPinOrder;
    }

    const leftPos = left.module.position ?? Number.MAX_SAFE_INTEGER;
    const rightPos = right.module.position ?? Number.MAX_SAFE_INTEGER;
    if (leftPos !== rightPos) return leftPos - rightPos;
    return left.displayName.localeCompare(right.displayName);
  });

  // Pages stay in newest-first order (already reversed in sidebarModuleItems)
  return [...nonPages, ...pages];
}
